import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiGrid,
  FiImage,
  FiPhone,
  FiX,
  FiMaximize2,
  FiMenu,
} from "react-icons/fi";

import { pexelsGallery, pexelsImages } from "../assets/images/pexelsImages";
import { useGetGalleryItemsQuery } from "../services/api";
import logo from "../assets/melona logo.png";

const CATEGORY_ICONS = {
  portfolio: <FiImage />,
  branding: <FiImage />,
  printing: <FiImage />,
  advertising: <FiImage />,
  digital: <FiImage />,
};

const PublicGalleryPage = () => {
  const { data } = useGetGalleryItemsQuery();
  const [activeFilter, setActiveFilter] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Portfolio Gallery - Melona Printing";
  }, []);

  const items = data?.data?.length ? data.data : pexelsGallery;

  const portfolioCategories = useMemo(() => {
    const categories = items.reduce((acc, item) => {
      const cat = item.category || "Portfolio";
      if (!acc.some((c) => c.id === cat.toLowerCase())) {
        acc.push({
          id: cat.toLowerCase(),
          label: cat,
          icon: CATEGORY_ICONS[cat.toLowerCase()] || <FiImage />,
        });
      }
      return acc;
    }, []);

    return [{ id: "all", label: "All Projects", icon: <FiGrid /> }, ...categories];
  }, [items]);

  const filteredPortfolio = useMemo(() => {
    if (activeFilter === "all") return items;
    return items.filter((item) => (item.category || "Portfolio").toLowerCase() === activeFilter);
  }, [items, activeFilter]);

  const openLightbox = (item) => {
    setLightboxImage(item);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <div className="melona-premium">
      {/* ── Navigation ── */}
      <nav className="melona-nav scrolled">
        <div className="melona-nav__inner">
          <Link to="/" className="melona-nav__logo">
            <img src={logo} alt="Melona" style={{ height: "40px" }} />
          </Link>
          <div className="melona-nav__links">
            <Link to="/">Home</Link>
            <Link to="/about">About</Link>
            <Link to="/services">Services</Link>
            <Link to="/gallery" className="active">Portfolio</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/login">Login</Link>
          </div>
          <Link to="/contact" className="melona-nav__cta">
            <FiPhone /> Get Quote
          </Link>
          <button className="melona-nav__toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="melona-mobile-menu">
            <nav>
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/services" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="active">Portfolio</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            </nav>
            <Link to="/contact" className="melona-btn melona-btn--primary melona-btn--full" style={{ marginTop: "var(--sp-4)" }} onClick={() => setMobileMenuOpen(false)}>
              <FiPhone /> Get Free Quote
            </Link>
          </div>
        )}
      </nav>

      {/* ── Hero Section ── */}
      <section className="melona-hero">
        <div className="melona-hero__bg">
          <img src={pexelsImages.hero} alt="Melona Portfolio" className="melona-hero__bg-img" />
          <div className="melona-hero__overlay" />
          <div className="melona-hero__particles">
            {[...Array(6)].map((_, i) => <div key={i} className={`particle particle-${i + 1}`} />)}
          </div>
        </div>
        <div className="melona-hero__content">
          <div className="melona-hero__text">
            <div className="melona-hero__badge">
              <span className="pulse-dot" />
              Portfolio Gallery
            </div>
            <h1 className="melona-hero__title">
              Explore Our Creative{" "}
              <span className="melona-hero__title-accent">Work Portfolio</span>
            </h1>
            <p className="melona-hero__subtitle">
              Browse through our collection of completed projects across printing, branding, 
              advertising, and digital marketing. Every piece showcases our commitment to quality and creativity.
            </p>
            <div className="melona-hero__actions">
              <Link to="/contact" className="melona-btn melona-btn--primary">
                Start Your Project <FiArrowRight />
              </Link>
              <a href="#portfolio-section" className="melona-btn melona-btn--outline">
                Browse Gallery
              </a>
            </div>
          </div>
          <div className="melona-hero__card">
            <div className="melona-hero__card-header">
              <div className="melona-hero__card-icon"><FiGrid /></div>
              <div>
                <h3>Gallery Stats</h3>
                <p>Our portfolio at a glance</p>
              </div>
            </div>
            <div className="melona-hero__card-items">
              <div className="melona-hero__card-item">
                <div className="melona-hero__card-item-icon"><FiImage /></div>
                <div>
                  <h4>{items.length}+ Projects</h4>
                  <p>Completed works</p>
                </div>
              </div>
              <div className="melona-hero__card-item">
                <div className="melona-hero__card-item-icon"><FiGrid /></div>
                <div>
                  <h4>{portfolioCategories.length - 1} Categories</h4>
                  <p>Different services</p>
                </div>
              </div>
              <div className="melona-hero__card-item">
                <div className="melona-hero__card-item-icon"><FiImage /></div>
                <div>
                  <h4>300+ Clients</h4>
                  <p>Satisfied customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="melona-hero__stats">
          <div className="melona-hero__stat">
            <span className="melona-hero__stat-value">{items.length}+</span>
            <span className="melona-hero__stat-label">Projects</span>
          </div>
          <div className="melona-hero__stat">
            <span className="melona-hero__stat-value">{portfolioCategories.length - 1}</span>
            <span className="melona-hero__stat-label">Categories</span>
          </div>
          <div className="melona-hero__stat">
            <span className="melona-hero__stat-value">300+</span>
            <span className="melona-hero__stat-label">Happy Clients</span>
          </div>
          <div className="melona-hero__stat">
            <span className="melona-hero__stat-value">98%</span>
            <span className="melona-hero__stat-label">Satisfaction</span>
          </div>
        </div>
      </section>

      {/* ── Portfolio Section ── */}
      <section className="melona-section" id="portfolio-section">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiGrid /> Browse Projects</span>
            <h2 className="melona-section-title">Explore Our Portfolio</h2>
            <p className="melona-section-subtitle">Click on any project to view it in full screen</p>
          </div>
          
          {/* Filters */}
          <div className="melona-portfolio-filters">
            {portfolioCategories.map((cat) => (
              <button
                key={cat.id}
                className={`melona-filter-btn ${activeFilter === cat.id ? "active" : ""}`}
                onClick={() => setActiveFilter(cat.id)}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {filteredPortfolio.length > 0 ? (
            <div className="melona-portfolio-grid">
              {filteredPortfolio.map((item) => (
                <div 
                  key={item.id} 
                  className="melona-portfolio-item"
                  onClick={() => openLightbox(item)}
                  style={{ cursor: "pointer" }}
                >
                  <img src={item.image_url} alt={item.title} />
                  <div className="melona-portfolio-item__overlay">
                    <h3>{item.title}</h3>
                    {item.description && <p>{item.description}</p>}
                    <div className="melona-portfolio-item__tags">
                      <span>{item.category || "Portfolio"}</span>
                      {item.featured && <span>Featured</span>}
                    </div>
                    <div style={{ marginTop: "1rem", display: "inline-flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", color: "#D4AF37" }}>
                      <FiMaximize2 /> Click to view
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: "60px 20px", textAlign: "center", color: "#888" }}>
              <FiImage size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
              <p style={{ fontSize: "1rem", fontWeight: 600 }}>No projects found</p>
              <p style={{ fontSize: "0.875rem" }}>Try selecting a different category</p>
            </div>
          )}

          {/* CTA */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "3rem" }}>
            <Link to="/contact" className="melona-btn melona-btn--primary">
              Request Similar Work <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxOpen && lightboxImage && (
        <div 
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.95)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            animation: "fadeIn 0.3s ease"
          }}
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: "absolute",
              top: "2rem",
              right: "2rem",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              border: "2px solid rgba(255,255,255,0.2)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "1.5rem",
              transition: "all 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <FiX />
          </button>
          
          <div 
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.5rem"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.image_url}
              alt={lightboxImage.title}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(90vh - 120px)",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
              }}
            />
            <div style={{ textAlign: "center", color: "#fff" }}>
              <h3 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "0.5rem" }}>
                {lightboxImage.title}
              </h3>
              {lightboxImage.description && (
                <p style={{ opacity: "0.8", marginBottom: "1rem" }}>{lightboxImage.description}</p>
              )}
              <div style={{ display: "inline-flex", gap: "1rem" }}>
                <span style={{ 
                  padding: "0.5rem 1rem", 
                  background: "rgba(255,255,255,0.1)", 
                  borderRadius: "50px",
                  fontSize: "0.875rem"
                }}>
                  {lightboxImage.category || "Portfolio"}
                </span>
                {lightboxImage.featured && (
                  <span style={{ 
                    padding: "0.5rem 1rem", 
                    background: "rgba(212,175,55,0.2)", 
                    borderRadius: "50px",
                    fontSize: "0.875rem",
                    color: "#D4AF37"
                  }}>
                    Featured
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CTA Strip ── */}
      <section className="melona-section melona-section--gray">
        <div className="melona-container">
          <div style={{
            background: "linear-gradient(135deg, #0F5D4F 0%, #22A06B 100%)",
            borderRadius: "24px",
            padding: "3rem 2rem",
            textAlign: "center",
            color: "#fff"
          }}>
            <h2 style={{ fontSize: "2rem", fontWeight: "800", marginBottom: "1rem" }}>
              Ready to Start Your Project?
            </h2>
            <p style={{ fontSize: "1.125rem", opacity: "0.95", marginBottom: "2rem", maxWidth: "600px", margin: "0 auto 2rem" }}>
              Let's create something amazing together. Contact us today for a free quote and consultation.
            </p>
            <Link 
              to="/contact" 
              className="melona-btn melona-btn--outline" 
              style={{ background: "#fff", color: "#0F5D4F", border: "none" }}
            >
              Get Started <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicGalleryPage;
